import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Button } from '@mui/material';
import { Icon, SimpleModal, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { FilePreviewPlain } from 'src/components/shared/FilePreviewModal/plainIndex';
import { getBillAutomationErrorList, getBillAutomationWarningList } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import { isInvoiceNumberFixed, resolveApPushInvoiceNumber } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { pickValue } from './BillDetails/billDetailsUtils';
import useApplyBillingStyles from 'src/pages/NewBillingModule/ApplyBillingFull/styles';
import { useStore } from 'src/stores';
import { BILL_AUTOMATION_STATUS } from 'src/stores/billAutomation/uploadedBills';
import { AllocationSuggestionsCard, BillDetailsCard, useBillDetailsStyles } from './BillDetails';
import { ConfirmActionModal } from './BillDetails/ConfirmActionModal';
import { isHeldByAppfolioPush } from './appfolioPushRow';
import { getAllocatedServices, getDeleteBlockedReason, getEditBlockedReason } from './companyBillRows';
import { useApGlAccounts } from './useApGlAccounts';
import { useBillApPushDetails } from './useBillApPushDetails';

/**
 * Read-only details of a bill uploaded from the Bills page: the attached file on the left, the
 * extraction result on the right (Figma: "Apply billing - default").
 */
export const UploadedBillDetailsModal = observer(props => {
  const appfolioPush = props.appfolioPush,
    bill = props.bill,
    canTriggerPush = props.canTriggerPush,
    handleClose = props.handleClose,
    listApPush = props.listApPush,
    onAttach = props.onAttach,
    onOpenBill = props.onOpenBill;
  const classes = useBillDetailsStyles();
  const bannerClasses = useApplyBillingStyles();
  const store = useStore();
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isConfirmingDelete = _useState2[0],
    setIsConfirmingDelete = _useState2[1];
  // Called before the early return below, because a hook cannot be conditional. It reads nothing
  // without a bill.
  const apPushDetails = useBillApPushDetails({
    billId: bill === null || bill === void 0 ? void 0 : bill.billId,
    isEnabled: !!bill && store.company.companyFeatureStore.isAppfolio,
    listApPush
  });
  /**
   * The GL codes flag gates the read as well as AppFolio: the service only fills the Accounts Payable
   * account in while that flag is on, so reading with it off would show every property as unconfigured
   * when the truth is that it was never sent.
   *
   * The push status comes in because a pushed bill answers a different question — where its payable
   * posted, not where one would post now.
   */
  const apGlAccounts = useApGlAccounts({
    isEnabled: !!bill && store.company.companyFeatureStore.isAppfolio && store.account.featureFlagsStore.getIsGlCodesEnabled,
    pushStatus: apPushDetails.pushStatus
  });

  // The stored document, previewed from a short-lived presigned URL when there is no in-session
  // upload blob to show — i.e. bills that arrived by email, or any bill reopened after a reload
  // (LB-5755 C1). Fetched fresh on open because the URL expires (~15 min) and is opened in an iframe
  // below, never fetch()'d (the bill bucket has no CORS). status: 'loading' until resolved, then
  // 'ready' with a url, or 'none' when the bill has no stored document.
  const _useState3 = useState({
      status: 'loading',
      url: ''
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    sourcePreview = _useState4[0],
    setSourcePreview = _useState4[1];
  const billId = bill === null || bill === void 0 ? void 0 : bill.billId;
  const hasSessionBlob = !!(bill !== null && bill !== void 0 && bill.fileUrl);
  useEffect(() => {
    if (!billId || hasSessionBlob) {
      setSourcePreview({
        status: 'loading',
        url: ''
      });
      return undefined;
    }
    let active = true;
    setSourcePreview({
      status: 'loading',
      url: ''
    });
    (async _resp$response => {
      const resp = await store.companyBills.getBillFileUrlAsync.run({
        billId
      });
      if (!active) return;
      const url = resp && resp.isSuccessful !== false ? (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.url : '';
      setSourcePreview(url ? {
        status: 'ready',
        url
      } : {
        status: 'none',
        url: ''
      });
    })();
    return () => {
      active = false;
    };
  }, [billId, hasSessionBlob, store.companyBills]);
  if (!bill) return null;
  const billResponse = {
    status: bill.status,
    data: bill.data,
    errors: [...(bill.errors || [])],
    warnings: [...(bill.warnings || [])]
  };
  const errorList = getBillAutomationErrorList(billResponse);
  const warningList = getBillAutomationWarningList(billResponse);

  // A bill whose property could not be matched is still fully extracted, so its data is worth showing.
  const isExtractionReady = bill.status === BILL_AUTOMATION_STATUS.validated || bill.status === BILL_AUTOMATION_STATUS.needsProperty;
  const extractedBillData = isExtractionReady && bill.data && errorList.length === 0 ? bill.effectiveData : null;
  const isAppfolio = store.company.companyFeatureStore.isAppfolio;
  // Read once for the whole view: matching the bill against them is the same question in both cards.
  const properties = store.company.propertiesList;
  const isProcessing = !bill.isFinalStatus;
  const isDuplicate = bill.status === BILL_AUTOMATION_STATUS.duplicate;
  const isRejected = bill.status === BILL_AUTOMATION_STATUS.rejected;
  // The poll payload carries neither the link nor the reason; the list row has both, and the modal
  // already reads it for other things.
  const listRow = isDuplicate || isRejected ? store.companyBills.getBill(bill.billId) : null;
  const duplicateOfBillId = isDuplicate && (listRow === null || listRow === void 0 ? void 0 : listRow.duplicateOfBillId) || null;
  // The reason normally arrives in the polled errors; when it has not (row opened before its poll,
  // or a message shape the error reader filters out), the row's statusReason is the same text.
  const reasonList = errorList.length > 0 ? errorList : listRow !== null && listRow !== void 0 && listRow.statusReason ? [listRow.statusReason] : [];
  const showErrorBanner = !isProcessing && (bill.status === BILL_AUTOMATION_STATUS.failed || isDuplicate || isRejected || errorList.length > 0);
  const showWarningBanner = !isProcessing && warningList.length > 0;
  const fileName = bill.fileName || 'Uploaded bill';

  // The list is what knows how far each service of the bill has got, and whether a payable exists for
  // it, so it is what says whether the bill can still be deleted. A bill the list has not caught up
  // with yet is left to the service.
  const listedBill = store.companyBills.getBill(bill.billId);
  const isAllocated = getAllocatedServices(listedBill === null || listedBill === void 0 ? void 0 : listedBill.utilities).length > 0;
  const isPushed = isHeldByAppfolioPush(listedBill === null || listedBill === void 0 ? void 0 : listedBill.apPush);
  const canDelete = !isAllocated && !isPushed;
  const deleteBlockedReason = getDeleteBlockedReason({
    isAllocated,
    isPushed
  });
  // Same two facts, and deliberately the same two: an edit guard that disagreed with the delete guard
  // about whether a bill is committed would be worse than no guard at all.
  const editBlockedReason = getEditBlockedReason({
    isAllocated,
    isPushed
  });
  const handleDelete = async () => {
    setIsConfirmingDelete(false);
    const resp = await store.companyBills.deleteBillAsync.run({
      billId: bill.billId
    });
    if (!resp) return;
    if (!resp.isSuccessful) {
      toast('error', {
        title: 'Bill not deleted',
        body: [resp.message || 'This bill has been allocated and can no longer be deleted.']
      });
      return;
    }
    store.uploadedBills.removeUploadedBill(bill.billId);
    handleClose();
    toast('success', {
      body: ["".concat(fileName, " deleted.")]
    });
  };
  const handleSaveBillData = data => store.uploadedBills.setEditedData(bill.billId, data);
  const handleSaveAllocationSettings = settings => store.uploadedBills.setAllocationSettings(bill.billId, settings);

  /**
   * The raw extraction says what OCR read and how confidently; the edit table is where a person can put
   * an invoice number the bill has none of. Judged apart, because `effectiveData` folds the two together
   * and a typed number must not be mistaken for a confidently extracted one.
   */
  const apPushInvoiceNumber = resolveApPushInvoiceNumber({
    extraction: bill.data,
    typedInvoiceNumber: bill.editedData ? pickValue(bill.editedData, ['invoiceNumber', 'invoiceNo']) : ''
  });

  /**
   * Gated on AppFolio as well as on confidence: without a push there is nothing that would ignore an
   * edit here, so locking the field would take something away for no reason a person could act on.
   */
  const isInvoiceNumberLocked = isAppfolio && isInvoiceNumberFixed(bill.data);

  // Read for this bill alone: the list poll rests while this view is open, and the blockers behind a
  // failure live on the attempt, which the list row does not carry.
  const pushState = apPushDetails.getState(bill.status, apPushInvoiceNumber);
  return /*#__PURE__*/React.createElement(SimpleModal, {
    handleClose: handleClose,
    isOpen: !!bill
    // Keeps the edit fields and the file preview mounted while the store updates around them.
    ,
    isMemoChildren: true,
    noFooter: true,
    noHeader: true,
    noPadding: true,
    variant: "flex"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.filePane
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.filePaneInner
  }, bill.fileUrl ? /*#__PURE__*/React.createElement("div", {
    className: classes.filePage
  }, /*#__PURE__*/React.createElement(FilePreviewPlain, {
    docUri: bill.fileUrl,
    fileId: "",
    fileName: bill.fileName,
    fileSize: bill.fileSizeMB,
    isDisabledParent: true,
    isPreviewOnly: true,
    key: bill.fileUrl
  })) : sourcePreview.status === 'ready' ?
  /*#__PURE__*/
  // Native browser render of the presigned URL. Not the DocViewer above: it XHRs the
  // document, and the bill bucket has no CORS, so a top-level iframe navigation is the
  // only way to show it (LB-5755).
  React.createElement("iframe", {
    className: classes.fileFrame,
    src: sourcePreview.url,
    title: bill.fileName || 'Bill document'
  }) : sourcePreview.status === 'none' ? /*#__PURE__*/React.createElement("div", {
    className: classes.filePlaceholder
  }, "No document is available to preview for this bill.") : /*#__PURE__*/React.createElement("div", {
    className: classes.filePlaceholder
  }, "Loading the bill document\u2026"))), /*#__PURE__*/React.createElement("div", {
    className: classes.dataPane
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.dataHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileCard
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileCardTitle
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "file-text"
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.fileCardName,
    title: fileName
  }, fileName)), /*#__PURE__*/React.createElement("button", {
    "aria-label": "Delete this bill",
    className: classes.iconButton,
    disabled: !canDelete,
    onClick: () => setIsConfirmingDelete(true),
    title: canDelete ? 'Delete this bill' : deleteBlockedReason,
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "trash"
  })), /*#__PURE__*/React.createElement("button", {
    "aria-label": "Close",
    className: classes.iconButton,
    onClick: handleClose,
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "close"
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.dataContent
  }, isProcessing ? /*#__PURE__*/React.createElement("div", {
    className: classes.dataContentBlock
  }, /*#__PURE__*/React.createElement("div", {
    "aria-busy": "true",
    "aria-live": "polite",
    className: "".concat(bannerClasses.extractedBanner, " ").concat(bannerClasses.extractedBannerLoadingOnly),
    role: "status"
  }, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: bannerClasses.extractedBannerSpinnerWrap,
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-sparkle-animated"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerTitle
  }, "Processing your bill"), /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerText
  }, "Please wait while we extract and validate the information from your uploaded file. This may take a moment."))))) : null, bill.extractionSeconds !== null ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.dataContentBlock, " ").concat(classes.thinkingLine)
  }, "Thought for ", bill.extractionSeconds, " s") : null, showErrorBanner ? /*#__PURE__*/React.createElement("div", {
    className: classes.dataContentBlock
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(bannerClasses.extractedBanner, " ").concat(bannerClasses.extractedBannerError, " ").concat(bannerClasses.extractedBannerLoadingOnly),
    role: "alert"
  }, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: bannerClasses.extractedBannerAiIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-error"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerErrorTitle
  }, isDuplicate ? 'This bill is a duplicate' : isRejected ? 'This bill was not accepted' : 'Extraction and validation could not be completed'), /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerErrorIntro
  }, isDuplicate ? 'It was not processed, because the company already holds it:' : isRejected ? 'It was rejected for the following reason(s):' : 'We were unable to process this bill for the following reason(s):'), reasonList.length > 0 ? /*#__PURE__*/React.createElement("ul", {
    className: bannerClasses.extractedBannerErrorList
  }, reasonList.map((msg, idx) => /*#__PURE__*/React.createElement("li", {
    key: idx,
    className: bannerClasses.extractedBannerErrorListItem
  }, msg))) : /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerErrorIntro
  }, "No detailed message was returned. Please confirm the file and property setup, then try again or contact support if the issue continues."), isDuplicate && duplicateOfBillId && onOpenBill ? /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => onOpenBill(duplicateOfBillId)
  }, "View the original bill") : null, (isRejected || bill.status === BILL_AUTOMATION_STATUS.needsProperty) && onAttach ?
  /*#__PURE__*/
  // The remediation itself, where the problem is shown (LB-5766): attach picks
  // the property, and can allow the sender so the next bill needs nobody.
  React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => {
      handleClose();
      onAttach(bill.billId);
    }
  }, "Attach to a property") : null)))) : null, showWarningBanner ? /*#__PURE__*/React.createElement("div", {
    className: classes.dataContentBlock
  }, /*#__PURE__*/React.createElement("div", {
    "aria-live": "polite",
    className: "".concat(bannerClasses.extractedBanner, " ").concat(bannerClasses.extractedBannerWarning, " ").concat(bannerClasses.extractedBannerLoadingOnly),
    role: "status"
  }, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerContent
  }, /*#__PURE__*/React.createElement("span", {
    className: bannerClasses.extractedBannerAiIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "ai-file-warning"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerWarningTitle
  }, "Review the following warnings"), /*#__PURE__*/React.createElement("div", {
    className: bannerClasses.extractedBannerWarningIntro
  }, "We detected a few items that may need your attention:"), /*#__PURE__*/React.createElement("ul", {
    className: bannerClasses.extractedBannerWarningList
  }, warningList.map((msg, idx) => /*#__PURE__*/React.createElement("li", {
    key: idx,
    className: bannerClasses.extractedBannerWarningListItem
  }, msg))))))) : null, extractedBillData ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.dataContentBlock, " ").concat(classes.introLine)
  }, "Extraction complete. Here's what we found:"), /*#__PURE__*/React.createElement(BillDetailsCard, {
    apGlAccounts: apGlAccounts,
    billPropertyIds: bill.assignedPropertyIds,
    customerName: store.admin.companyName,
    data: extractedBillData,
    editBlockedReason: editBlockedReason,
    isAppfolio: isAppfolio,
    isInvoiceNumberLocked: isInvoiceNumberLocked,
    onPush: () => appfolioPush.handlePush(bill.billId, apPushInvoiceNumber.invoiceNumber),
    onSave: handleSaveBillData,
    properties: properties,
    pushRefusalCode: appfolioPush.getRefusalCode(bill.billId),
    pushState: pushState,
    showPush: canTriggerPush
  }), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.dataContentBlock, " ").concat(classes.introLine)
  }, "We've prepared allocation tables for each utility. Please review and approve charges"), /*#__PURE__*/React.createElement(AllocationSuggestionsCard, {
    billId: bill.billId,
    currency: extractedBillData.currency,
    data: extractedBillData,
    onSaveSettings: handleSaveAllocationSettings,
    properties: properties,
    propertyIds: bill.assignedPropertyIds,
    settings: bill.allocationSettings
  })) : null))), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Delete bill",
    isOpen: isConfirmingDelete,
    message: "".concat(fileName, " will be deleted, along with everything read from it. This cannot be undone."),
    onClose: () => setIsConfirmingDelete(false),
    onConfirm: handleDelete,
    title: "Delete bill?"
  }));
});